import {Action, Selector, State, StateContext} from '@ngxs/store';
import {Injectable} from '@angular/core';
import {PolicyPg} from '../../models/policy.model';
import {PolicyPgActions} from './policy-pg.actions';
import {PolicyService} from '../../services/policy.service';
import {SalesCpiStateModel} from '../sales-cpi/sales-cpi.state';
import {ModalService} from '../../services/modal.service';
import {TranslateService} from '@ngx-translate/core';
import {PolicyPgDownloadModalComponent} from '../../modules/policy/components/pg-policy-modal';
import {SalesPgService} from '../../services/sales-pg.service';
import {catchError, tap} from 'rxjs/operators';
import {Claims} from '../../models/claims.model';
import {HttpErrorResponse} from '@angular/common/http';
import {EMPTY, throwError} from 'rxjs';
import {ClaimsService} from '../../services/claims.service';
import {FormState} from '../../models/form-state.model';
import {DictionaryItem} from '../../models/dictionary.model';
import {
    PolicyPgTerminationAsideComponent
} from '../../modules/policy/pages/policy-pg-view/components/policy-pg-termination-aside/policy-pg-termination-aside.component';
import {AsideService} from '../../services/aside.service';
import {setFormValidationErrors} from '../../helpers/form.helper';

interface PolicyPgStateModel {
    activePolicy: PolicyPg;
    claims: Claims;
    form: FormState;
    terminationReasons: DictionaryItem[];
}

const getDefaultState = () => ({
    activePolicy: null,
    claims: null,
    form: new FormState(),
    terminationReasons: null,
});

@State<PolicyPgStateModel>({
    name: PolicyPgState.STATE_NAME,
    defaults: getDefaultState()
})

@Injectable()
export class PolicyPgState {
    static readonly STATE_NAME = 'policyPg';

    constructor(
        private readonly policyService: PolicyService,
        private readonly modalService: ModalService,
        private readonly translateService: TranslateService,
        private readonly salesPgService: SalesPgService,
        private readonly claimsService: ClaimsService,
        private readonly asideService: AsideService,
    ) {
    }

    @Selector()
    static activePolicy(state: PolicyPgStateModel) {
        return state?.activePolicy;
    }

    @Selector()
    static claims(state: PolicyPgStateModel) {
        return state?.claims;
    }

    @Selector()
    static terminationReasons(state: PolicyPgStateModel) {
        return state?.terminationReasons;
    }

    @Selector()
    static formErrors(state: PolicyPgStateModel) {
        return state?.form?.validationErrors;
    }

    @Action(PolicyPgActions.FetchById)
    fetchById(ctx: StateContext<PolicyPgStateModel>, {id}: PolicyPgActions.FetchById) {
        return this.policyService.getPolicy(id).subscribe(
            (policy) => {
                ctx.patchState({activePolicy: new PolicyPg(policy)});
            }
        );
    }

    @Action(PolicyPgActions.Clear)
    clear(ctx: StateContext<PolicyPgStateModel>) {
        ctx.patchState(getDefaultState());
    }

    @Action(PolicyPgActions.PaymentConfirmation)
    paymentConfirmation(ctx: StateContext<PolicyPgStateModel>, {id}: PolicyPgActions.PaymentConfirmation) {
        this.policyService.confirmPayment(id).subscribe(policy => {
            ctx.patchState({activePolicy: new PolicyPg(policy)});
            ctx.dispatch(new PolicyPgActions.OpenDownloadPolicyModal());
        });
    }

    @Action(PolicyPgActions.OpenDownloadPolicyModal)
    openDownloadPolicyModal(ctx: StateContext<PolicyPgStateModel>) {
        const policy = ctx.getState().activePolicy;
        const modalRef = this.modalService.generic({
            nzContent: PolicyPgDownloadModalComponent,
            nzFooter: [
                {
                    label: this.translateService.instant('SALES_PG.POLICY.OPERATION.PRINTOUT_DOWNLOAD'),
                    type: 'default',
                    onClick: () => {
                        ctx.dispatch(
                            new PolicyPgActions.DownloadPolicyPrintout(policy?.id?.toString(), true)
                        );
                        modalRef.close();
                    }
                },
                {
                    label: this.translateService.instant('LABELS.CLOSE'),
                    type: 'primary',
                    onClick: () => {
                        modalRef.close();
                    },
                }
            ],
            nzComponentParams: {policy},
        });
    }

    @Action(PolicyPgActions.GetInstallmentsAndClaims)
    getInstallmentsAndClaims(ctx: StateContext<PolicyPgStateModel>) {
        const policyId = ctx.getState().activePolicy.id;
        return this.claimsService.getInstallmentsAndClaims(policyId).pipe(
            tap((claims) => {
                    ctx.patchState({claims: new Claims(claims)});
                }
            ),
            catchError((error: HttpErrorResponse) => {
                if (error.status === 404) {
                    this.modalService.error({
                        nzContent: this.translateService.instant('LABELS.CLAIMS_NOT_FOUND')
                    });
                }
                return throwError(error);
            })
        );
    }

    @Action(PolicyPgActions.DownloadPolicyPrintout)
    downloadPolicyPrintout(ctx: StateContext<SalesCpiStateModel>, {policyId, addKidOwu}: PolicyPgActions.DownloadPolicyPrintout) {
        this.salesPgService.downloadPrintout(policyId, addKidOwu);
    }

    @Action(PolicyPgActions.CancelPolicy)
    cancelPolicy(ctx: StateContext<PolicyPgStateModel>, {id}: PolicyPgActions.CancelPolicy) {
        this.policyService.pgCancellation(id).subscribe(policy => {
            ctx.patchState({activePolicy: new PolicyPg(policy)});
        });
    }

    @Action(PolicyPgActions.TerminatePolicyAside)
    terminatePolicyAside(ctx: StateContext<PolicyPgStateModel>, {policyId}: PolicyPgActions.TerminatePolicyAside) {
        return this.policyService.getTerminationReasons(policyId)
            .pipe(
                tap((terminationReasons) => {
                    ctx.patchState({terminationReasons});
                    this.asideService.open(PolicyPgTerminationAsideComponent, {terminationReasons, policyId});
                }));
    }

    @Action(PolicyPgActions.TerminatePolicy)
    terminatePolicy(ctx: StateContext<PolicyPgStateModel>, {policyId, payload}: PolicyPgActions.TerminatePolicy) {
        return this.policyService.pgTermination(policyId, payload).pipe(
            tap((policy) => {
                ctx.patchState({activePolicy: new PolicyPg(policy)});
                this.asideService.closeAside();
            }),
            catchError((errorResponse: HttpErrorResponse) => {
                ctx.dispatch(new PolicyPgActions.FormFailure(errorResponse.error));
                return EMPTY;
            })
        );
    }

    @Action(PolicyPgActions.FormFailure)
    addLocalizationFailure(
        ctx: StateContext<PolicyPgStateModel>,
        {validationErrors = []}: PolicyPgActions.FormFailure
    ) {
        setFormValidationErrors(ctx, validationErrors, PolicyPgState.STATE_NAME);
    }


}
